// app/src/pages/BillingPage.tsx
//
// Serves as the paywall gate. Shown when an org has no active subscription.
//
// Two modes:
//   Normal:  show embedded Stripe Checkout for admins; "contact admin" for members.
//   Return:  show success message when Stripe redirects back with ?session_id=...
//            then poll billing status and redirect to /dashboard once active.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import type { StripeEmbeddedCheckout } from '@stripe/stripe-js';
import { useAuth } from '../auth/AuthContext';
import { useBilling } from '../context/BillingContext';
import { api } from '../lib/apiClient';

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string,
);

type CheckoutMode = 'subscription' | 'pilot';

export function BillingPage() {
  const { user, orgRole, orgId, loading: authLoading, signOut } = useAuth();
  const { isActive, isLoading, refetch } = useBilling();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const sessionId = searchParams.get('session_id');
  const isAdmin = orgRole === 'admin';

  const handleGoToLogin = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  // ── All hooks must be declared before any conditional returns ───────────────
  const checkoutRef = useRef<StripeEmbeddedCheckout | null>(null);
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutMode, setCheckoutMode] = useState<CheckoutMode>('subscription');
  const hasPollStarted = useRef(false);

  const pollForActive = useCallback(() => {
    if (hasPollStarted.current) return;
    hasPollStarted.current = true;

    let attempts = 0;
    const maxAttempts = 10;

    const poll = () => {
      attempts++;
      refetch();
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      }
    };

    // Give the webhook a 2-second head start
    setTimeout(poll, 2000);
  }, [refetch]);

  // ── Mount embedded checkout for admins ─────────────────────────────────────
  useEffect(() => {
    if (!isAdmin) return;
    if (!orgId) return;       // Org context not yet resolved
    if (sessionId) return;    // Return URL mode — don't mount checkout
    if (isLoading) return;
    if (isActive) return;     // Shouldn't reach here, but guard anyway

    let cancelled = false;

    (async () => {
      try {
        const stripe = await stripePromise;
        if (!stripe || cancelled) return;

        const endpoint =
          checkoutMode === 'pilot' ? '/billing/checkout-pilot' : '/billing/checkout';
        const data = await api.post<{ ok: boolean; clientSecret: string }>(endpoint);
        if (cancelled) return;

        const checkout = await stripe.initEmbeddedCheckout({
          clientSecret: data.clientSecret,
        });

        if (cancelled) {
          checkout.destroy();
          return;
        }

        checkoutRef.current = checkout;
        if (mountRef.current) {
          checkout.mount(mountRef.current);
        }
      } catch (err: any) {
        if (!cancelled) {
          setCheckoutError(err.message ?? 'Failed to load checkout. Please refresh.');
        }
      }
    })();

    return () => {
      cancelled = true;
      checkoutRef.current?.destroy();
      checkoutRef.current = null;
    };
  }, [isAdmin, orgId, sessionId, isLoading, isActive, checkoutMode]);

  // ── Handle return from Stripe Checkout ─────────────────────────────────────
  useEffect(() => {
    if (!sessionId) return;
    pollForActive();
  }, [sessionId, pollForActive]);

  // Redirect once the subscription is confirmed active
  useEffect(() => {
    if (isActive && sessionId) {
      navigate('/dashboard', { replace: true });
    }
  }, [isActive, sessionId, navigate]);

  // ── Loading state ───────────────────────────────────────────────────────────
  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500" />
      </div>
    );
  }

  // Redirect unauthenticated users to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If already active (and not on the return URL), skip to dashboard
  if (isActive && !sessionId) {
    return <Navigate to="/dashboard" replace />;
  }

  // ── Return from Stripe — waiting for webhook ────────────────────────────────
  if (!orgId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="text-center max-w-sm px-6">
          <h1 className="text-xl font-semibold text-slate-100 mb-2">
            Organization not found
          </h1>
          <p className="text-slate-400 text-sm">
            We couldn&apos;t find an organization for this session. Sign in again or use an invite link from your team.
          </p>
          <button
            type="button"
            onClick={handleGoToLogin}
            className="mt-5 rounded-md border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-medium text-slate-200 hover:border-amber-500 hover:text-amber-300"
          >
            Go to login
          </button>
        </div>
      </div>
    );
  }

  if (sessionId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="text-center max-w-sm px-6">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 mx-auto mb-5">
            <svg
              className="w-8 h-8 text-amber-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-slate-100 mb-2">
            Payment received
          </h1>
          <p className="text-slate-400 text-sm">
            Activating your subscription&hellip;
          </p>
          <div className="mt-6">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-500 mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  // ── Non-admin: cannot subscribe themselves ──────────────────────────────────
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="text-center max-w-sm px-6">
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-slate-800 mx-auto mb-5">
            <svg
              className="w-7 h-7 text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-slate-100 mb-2">
            Subscription required
          </h1>
          <p className="text-slate-400 text-sm">
            Your organization doesn&apos;t have an active ScopeShield
            subscription. Contact your organization admin to set up billing.
          </p>
          <button
            type="button"
            onClick={handleGoToLogin}
            className="mt-5 rounded-md border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-medium text-slate-200 hover:border-amber-500 hover:text-amber-300"
          >
            Go to login
          </button>
        </div>
      </div>
    );
  }

  // ── Admin: show plan selector + embedded Stripe Checkout ───────────────────
  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-start py-16 px-4">
      <div className="absolute right-4 top-4">
        <button
          type="button"
          onClick={handleGoToLogin}
          className="rounded-md border border-slate-800 bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-300 hover:border-amber-500 hover:text-amber-300"
        >
          Go to login
        </button>
      </div>
      <div className="w-full max-w-xl">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-slate-100 mb-2">
            Activate ScopeShield
          </h1>
          <p className="text-slate-400 text-sm">
            Choose a plan to get started.
          </p>
        </div>

        {/* Plan toggle */}
        <div className="flex rounded-lg border border-slate-800 bg-slate-900/60 p-1 mb-4 gap-1">
          <button
            type="button"
            onClick={() => {
              setCheckoutMode('subscription');
              setCheckoutError(null);
            }}
            className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
              checkoutMode === 'subscription'
                ? 'bg-amber-500 text-slate-900'
                : 'text-slate-400 hover:text-slate-100'
            }`}
          >
            Per-seat subscription
          </button>
          <button
            type="button"
            onClick={() => {
              setCheckoutMode('pilot');
              setCheckoutError(null);
            }}
            className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
              checkoutMode === 'pilot'
                ? 'bg-amber-500 text-slate-900'
                : 'text-slate-400 hover:text-slate-100'
            }`}
          >
            Concierge Margin Audit ($5,000)
          </button>
        </div>

        <p className="text-center text-xs text-slate-500 mb-6">
          {checkoutMode === 'subscription'
            ? 'Billed per team member. Add or remove seats at any time from your account settings.'
            : 'One-time payment for 30 days of full access. No ongoing commitment.'}
        </p>

        {checkoutError ? (
          <div className="rounded-lg border border-red-500/40 bg-red-950/30 px-4 py-3 text-sm text-red-200">
            {checkoutError}
          </div>
        ) : (
          <div
            id="checkout"
            ref={mountRef}
            className="w-full"
          />
        )}
      </div>
    </div>
  );
}

export default BillingPage;
